/**
 * Loading placeholders shaped like the thing that is coming.
 *
 * Cloud is slower to first paint than it looks: the shell waits on the session,
 * the config and the storage figure, and a file list then decrypts every name in
 * the browser before it can show one. A centred "Loading…" for two seconds reads
 * as a stall rather than as progress, and when the content does land the layout
 * jumps because nothing was holding its place.
 *
 * These are deliberately *specific* — a file row, a workspace card, a message —
 * rather than one generic grey box. A skeleton that matches the shape it is
 * standing in for reserves the right amount of space, so nothing moves when the
 * real thing arrives. That, rather than the shimmer, is the point.
 *
 * `aria-hidden` throughout, with the live region on the container: a screen
 * reader should hear "loading files", not eight unlabelled boxes.
 */

import React from 'react';
import { Panel } from './Shell';
import { useT } from '../i18n';

/** One grey block. `w` accepts any CSS length; a percentage is usually right. */
export function SkeletonBar({
  w = '100%',
  h = 12,
  radius = 6,
  style
}: {
  w?: number | string;
  h?: number;
  radius?: number;
  style?: React.CSSProperties;
}) {
  return (
    <span
      className="mv-skeleton"
      aria-hidden="true"
      style={{
        display: 'block',
        width: typeof w === 'number' ? `${w}px` : w,
        height: h,
        borderRadius: radius,
        ...style
      }}
    />
  );
}

/** Wraps a set of skeletons and announces what is being waited for. */
export function SkeletonRegion({
  label,
  children,
  style
}: {
  label: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" style={style}>
      <span
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap'
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

/**
 * Rows in a file or trash listing.
 *
 * Widths vary per row from a fixed sequence rather than at random: a re-render
 * mid-load must not reshuffle the placeholder, and `Math.random()` in a render
 * body would do exactly that.
 */
const NAME_WIDTHS = ['62%', '44%', '71%', '38%', '55%', '66%', '48%', '58%'];

export function SkeletonRows({ rows = 6, label = 'Loading' }: { rows?: number; label?: string }) {
  return (
    <SkeletonRegion label={label} style={{ display: 'grid' }}>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '13px 22px',
            borderBottom: i === rows - 1 ? 'none' : '1px solid var(--border-subtle)'
          }}
        >
          <SkeletonBar w={26} h={26} radius={8} style={{ flex: '0 0 auto' }} />
          <SkeletonBar w={NAME_WIDTHS[i % NAME_WIDTHS.length]} h={12} />
          <SkeletonBar w={64} h={10} style={{ marginLeft: 'auto', flex: '0 0 auto' }} />
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** Cards in the workspace grid. */
export function SkeletonCards({ count = 3, label = 'Loading workspaces' }: { count?: number; label?: string }) {
  return (
    <SkeletonRegion
      label={label}
      style={{
        display: 'grid',
        gap: 16,
        gridTemplateColumns: 'repeat(auto-fill, minmax(268px, 1fr))'
      }}
    >
      {Array.from({ length: count }, (_, i) => (
        <Panel key={i} style={{ padding: '20px 22px', display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <SkeletonBar w={34} h={34} radius={10} style={{ flex: '0 0 auto' }} />
            <SkeletonBar w="58%" h={13} />
          </div>
          <SkeletonBar w="42%" h={10} />
          <SkeletonBar w="34%" h={10} />
        </Panel>
      ))}
    </SkeletonRegion>
  );
}

/**
 * Chat messages.
 *
 * Alternating alignment and varying bubble length, because a column of
 * identical blocks does not read as a conversation.
 */
const BUBBLES = ['74%', '46%', '61%', '38%', '68%'];

export function SkeletonMessages({ count = 5 }: { count?: number }) {
  const t = useT();
  return (
    <SkeletonRegion label={t('skeleton.loading-messages')} style={{ display: 'grid', gap: 14 }}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} style={{ display: 'grid', gap: 6 }}>
          <SkeletonBar w={i % 2 ? 74 : 92} h={10} />
          <SkeletonBar w={BUBBLES[i % BUBBLES.length]} h={14} />
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** A stack of plain lines, for panels with no distinctive shape. */
export function SkeletonLines({
  lines = 3,
  label = 'Loading',
  style
}: {
  lines?: number;
  label?: string;
  style?: React.CSSProperties;
}) {
  return (
    <SkeletonRegion label={label} style={{ display: 'grid', gap: 10, ...style }}>
      {Array.from({ length: lines }, (_, i) => (
        <SkeletonBar key={i} w={i === lines - 1 ? '52%' : '100%'} h={11} />
      ))}
    </SkeletonRegion>
  );
}
